import { GroupedService as NewGroupedService } from '@Services/types/service.types';

/**
 * State for service selection and ordering flow.
 * Manages user selections and service metadata.
 * Matches portal-app's clean pattern: selections + metadata only.
 * Web-specific fields (currentStep) are kept for web functionality.
 */
interface ServicesState {
  /** Map of rowId to selectedQuantity for user selections */
  selections: Record<string, number>;
  /** Map of rowId to service metadata (stored when data is loaded) */
  serviceMetadata: Record<string, NewGroupedService>;
  /** Timestamp when "Snarast möjligast" is selected, null otherwise */
  earliestPossible: number | null;
  /** Selected date in YYYY-MM-DD format, null otherwise */
  selectedDate: string | null;
  /** Optional comment/message from the user */
  comment: string;
  /** Current step in the order flow (web-specific) */
  currentStep: 'selection' | 'checkout' | 'confirmation';
}

/**
 * Actions for updating services state.
 * All state updates go through these actions to ensure immutability.
 */
export type ServicesAction =
  | {
      type: 'UPDATE_SERVICE_QUANTITY';
      payload: { rowId: string; quantity: number };
    }
  | { type: 'RESET_SELECTIONS' }
  | { type: 'SET_SERVICE_METADATA'; payload: NewGroupedService[] }
  | { type: 'SET_EARLIEST_POSSIBLE'; payload: number | null }
  | { type: 'SET_SELECTED_DATE'; payload: string | null }
  | { type: 'SET_COMMENT'; payload: string }
  | { type: 'SET_CURRENT_STEP'; payload: 'selection' | 'checkout' | 'confirmation' }
  | { type: 'RESET_STEP' };

export const initialServicesState: ServicesState = {
  selections: {},
  serviceMetadata: {},
  earliestPossible: Date.now(), // Default to "Snarast möjligast"
  selectedDate: null,
  comment: '',
  currentStep: 'selection',
};

/**
 * Reducer for services state management.
 * Handles all state updates immutably, ensuring React re-renders correctly.
 *
 * @param state - Current services state
 * @param action - Action to perform
 * @returns New state object (never mutates input state)
 */
export function servicesReducer(state: ServicesState, action: ServicesAction): ServicesState {
  switch (action.type) {
    case 'UPDATE_SERVICE_QUANTITY': {
      const { rowId, quantity } = action.payload;
      const updatedSelections = { ...state.selections };

      if (quantity === 0) {
        delete updatedSelections[rowId];
      } else {
        updatedSelections[rowId] = quantity;
      }

      return {
        ...state,
        selections: updatedSelections,
      };
    }

    case 'RESET_SELECTIONS': {
      return {
        ...state,
        selections: {},
        earliestPossible: Date.now(),
        selectedDate: null,
        comment: '',
      };
    }

    case 'SET_SERVICE_METADATA': {
      const metadata: Record<string, NewGroupedService> = {};
      action.payload.forEach((service) => {
        metadata[service.rowId] = {
          ...service,
          selectedQuantity: 0,
        };
      });
      return {
        ...state,
        serviceMetadata: { ...state.serviceMetadata, ...metadata },
      };
    }

    case 'SET_EARLIEST_POSSIBLE': {
      return {
        ...state,
        earliestPossible: action.payload,
        selectedDate: action.payload !== null ? null : state.selectedDate,
      };
    }

    case 'SET_SELECTED_DATE': {
      return {
        ...state,
        selectedDate: action.payload,
        earliestPossible: action.payload !== null ? null : state.earliestPossible,
      };
    }

    case 'SET_COMMENT': {
      return {
        ...state,
        comment: action.payload,
      };
    }

    case 'SET_CURRENT_STEP': {
      return {
        ...state,
        currentStep: action.payload,
      };
    }

    case 'RESET_STEP': {
      return {
        ...state,
        currentStep: 'selection',
      };
    }

    default:
      return state;
  }
}
